import { useState, useRef, useEffect, useCallback } from 'react';
import { cleanDisplayText } from '@/utils/display-text';

interface InlineEditProps {
  value: string;
  onSave: (newValue: string) => Promise<void> | void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  /** When true, enters edit mode externally (e.g. from a menu action). */
  externalEditing?: boolean;
  /** Called when editing state changes (save/cancel). Use to reset externalEditing. */
  onEditingChange?: (editing: boolean) => void;
  /** When true, disables click-to-edit on the text span. Editing only via externalEditing. */
  disableClickToEdit?: boolean;
  /** When true, hides the dashed-underline hover affordance. */
  hideHoverHint?: boolean;
}

/**
 * Generic inline-edit component. Displays text that becomes an input on click
 * (or when externalEditing is set). Saves on Enter or blur, reverts on Escape.
 */
export function InlineEdit({
  value,
  onSave,
  placeholder = 'Untitled',
  className,
  inputClassName,
  externalEditing,
  onEditingChange,
  disableClickToEdit,
  hideHoverHint,
}: InlineEditProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saveState, setSaveState] = useState<'idle' | 'saving'>('idle');
  const inputRef = useRef<HTMLInputElement>(null);
  const optimisticRef = useRef<string | null>(null);
  const savingRef = useRef(false);
  // Escape sets this so the imminent onBlur=save sees the cancellation and
  // bails before persisting the in-progress draft. Without it, the input
  // unmounts before setDraft(value) lands, and save() reads the stale edit.
  const cancellingRef = useRef(false);

  // Enter edit mode when externalEditing becomes true
  useEffect(() => {
    if (externalEditing && !editing) {
      setEditing(true);
    }
  }, [externalEditing, editing]);

  // Sync external value changes when not editing.
  useEffect(() => {
    if (!editing) {
      if (optimisticRef.current !== null && value === optimisticRef.current) {
        optimisticRef.current = null;
      }
      if (optimisticRef.current === null) {
        setDraft(value);
      }
    }
  }, [value, editing]);

  // Auto-focus and select text when entering edit mode.
  // Uses rAF to run after any pending focus-restoration (e.g. Radix dropdown close).
  useEffect(() => {
    if (editing && inputRef.current) {
      const raf = requestAnimationFrame(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      });
      return () => cancelAnimationFrame(raf);
    }
  }, [editing]);

  const exitEditing = useCallback(() => {
    setEditing(false);
    onEditingChange?.(false);
  }, [onEditingChange]);

  const save = useCallback(async () => {
    if (cancellingRef.current) {
      cancellingRef.current = false;
      return;
    }
    if (savingRef.current) return;
    savingRef.current = true;

    const trimmed = draft.trim();
    const displayValue = trimmed || value;

    optimisticRef.current = displayValue;
    setDraft(displayValue);
    exitEditing();

    if (trimmed !== value) {
      setSaveState('saving');
      try {
        await onSave(trimmed);
        setSaveState('idle');
      } catch {
        optimisticRef.current = null;
        setDraft(value);
        setSaveState('idle');
      }
    } else {
      optimisticRef.current = null;
    }

    savingRef.current = false;
  }, [draft, value, onSave, exitEditing]);

  const cancel = useCallback(() => {
    cancellingRef.current = true;
    setDraft(value);
    exitEditing();
  }, [value, exitEditing]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={draft}
        placeholder={placeholder}
        className={`bg-transparent border-b border-primary outline-none w-full ${inputClassName ?? ''}`}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') cancel();
        }}
        onBlur={save}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
    );
  }

  if (disableClickToEdit) {
    const displayText = draft ? cleanDisplayText(draft) : placeholder;
    return (
      <span
        className={className ?? ''}
        style={{
          opacity: saveState === 'idle' ? 1 : 0.45,
          transition: 'opacity 800ms ease',
        }}
        // Full (cleaned) name on hover — these spans are usually truncated
        // list rows.
        title={displayText}
      >
        {displayText}
      </span>
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      className={`cursor-text ${hideHoverHint ? '' : 'hover:border-b hover:border-dashed hover:border-muted-foreground/50'} ${className ?? ''}`}
      style={{
        opacity: saveState === 'idle' ? 1 : 0.45,
        transition: 'opacity 800ms ease',
      }}
      onClick={(e) => {
        e.stopPropagation();
        setEditing(true);
        onEditingChange?.(true);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.stopPropagation();
          e.preventDefault();
          setEditing(true);
          onEditingChange?.(true);
        }
      }}
      title="Click to rename"
    >
      {/* Display-only cleanup (markdown/entities in agent-generated names).
          The edit input and onSave still see the raw `draft` — cleaning it
          there would silently rewrite stored titles on the first rename. */}
      {draft ? cleanDisplayText(draft) : placeholder}
    </span>
  );
}
